import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl } from "@/lib/api";

const ProductsListPage = ({ type, title, subtitle, eyebrow }) => {
  const { lang, t } = useI18n();
  const [params] = useSearchParams();
  const initialCat = params.get("cat") || "";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState(initialCat);

  useEffect(() => {
    api.get(`/categories?type=${type}`).then((r) => setCategories(r.data));
    api.get(`/products?type=${type}`).then((r) => setProducts(r.data));
  }, [type]);

  const filtered = filter
    ? products.filter((p) => {
        const cat = categories.find((c) => c.id === p.category_id);
        return cat?.slug === filter;
      })
    : products;

  return (
    <div data-testid={`${type}-list-page`}>
      <section className="bg-[#0A1128] text-white py-24 md:py-28">
        <div className="container-nakia">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-divider" />
            <span className="text-eyebrow text-[#D4AF37]">{eyebrow}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">{title}</h1>
          <p className="text-white/75 max-w-2xl mt-6">{subtitle}</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container-nakia">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("")}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold rounded-sm border transition-colors ${
                !filter ? "bg-[#0A1128] text-[#D4AF37] border-[#0A1128]" : "border-slate-300 text-slate-600 hover:border-[#0A1128]"
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`filter-${c.slug}`}
                onClick={() => setFilter(c.slug)}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold rounded-sm border transition-colors ${
                  filter === c.slug ? "bg-[#0A1128] text-[#D4AF37] border-[#0A1128]" : "border-slate-300 text-slate-600 hover:border-[#0A1128]"
                }`}
              >
                {getMl(c.name, lang)}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-20">{t("common.noResults")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  data-testid={`product-card-${p.id}`}
                  className="group card-light"
                >
                  <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                    {p.images?.[0] && (
                      <img src={p.images[0]} alt={getMl(p.name, lang)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                  </div>
                  <div className="p-7">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{p.origin_country || "—"}</div>
                    <h3 className="font-display text-xl text-[#0A1128] mb-2 group-hover:text-[#D4AF37] transition-colors">
                      {getMl(p.name, lang)}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{getMl(p.short_description, lang)}</p>
                    <div className="mt-5 flex items-center justify-between text-xs">
                      <span className="text-slate-500">MOQ: {p.moq || "—"}</span>
                      <span className="text-[#D4AF37] uppercase tracking-[0.2em] font-semibold">View →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductsListPage;
